import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import DiffPreviewModal from "@/components/DiffPreviewModal";
import { Sparkles, Loader2, Database, CheckCircle2, AlertCircle, Wand2, History, X, Play, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const DEFAULT_PDF = "https://customer-assets.emergentagent.com/job_whitelabel-market/artifacts/25b9yrkj_Ciemme%20%202026%20COMPLETO.pdf";
const LS_KEY = "tk_ai_import_active_job";
const ACTIVE_STATUSES = ["queued", "downloading", "ai_processing"];

export default function CasaMadreAiImport() {
  const [pdfUrl, setPdfUrl] = useState(DEFAULT_PDF);
  const [parsing, setParsing] = useState(false);
  const [progress, setProgress] = useState("");
  const [parsed, setParsed] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [lastJobId, setLastJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [resumeBanner, setResumeBanner] = useState(null); // job attivo trovato al mount
  const [diffOpen, setDiffOpen] = useState(false);
  const pollingRef = useRef(false); // evita doppi polling loop

  // ─── Carica lista job (storico) ──────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/ai/parse-jobs?limit=15");
      setJobs(r.data || []);
      return r.data || [];
    } catch (e) {
      return [];
    }
  }, []);

  // ─── Polling loop condiviso (resume + start) ─────────────────────────────
  const pollJob = useCallback(async (jobId, opts = {}) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setParsing(true);
    setError(null);
    if (!opts.keepParsed) setParsed(null);
    setLastJobId(jobId);
    localStorage.setItem(LS_KEY, jobId);
    const start = Date.now();
    try {
      while (true) {
        await new Promise((res) => setTimeout(res, 5000));
        const elapsed = Math.floor((Date.now() - start) / 1000);
        let s;
        try {
          s = await api.get(`/ai/parse-job/${jobId}?include_data=false`);
        } catch (e) {
          // job non trovato o rete → esci
          setError("Job non trovato o errore di rete");
          break;
        }
        const p = s.data.progress;
        const stepLabel = p ? ` · pass ${p.idx}/${p.total} (${p.step})` : "";
        setProgress(`Stato: ${s.data.status}${stepLabel} · ${elapsed}s da polling`);
        if (s.data.status === "done") {
          const full = await api.get(`/ai/parse-job/${jobId}?include_data=true`);
          setParsed(full.data);
          localStorage.removeItem(LS_KEY);
          toast.success(`AI ha estratto ${full.data.stats.tessuti_totali} tessuti · ${full.data.stats.gambe} gambe · ${full.data.stats.metalli} metalli · ${full.data.stats.products} prodotti`);
          break;
        }
        if (s.data.status === "error") {
          setError(s.data.error || "Errore sconosciuto");
          localStorage.removeItem(LS_KEY);
          toast.error("Errore: " + (s.data.error || "??"));
          break;
        }
        if (elapsed > 3600) {
          setError("Timeout dopo 60 minuti — il job potrebbe essere bloccato");
          break;
        }
      }
    } finally {
      setParsing(false);
      setProgress("");
      pollingRef.current = false;
      loadJobs();
    }
  }, [loadJobs]);

  // ─── Resume al mount ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const list = await loadJobs();
      // 1) prova a riprendere il job salvato in localStorage
      const savedId = localStorage.getItem(LS_KEY);
      const savedJob = savedId ? list.find((j) => j.id === savedId) : null;
      if (savedJob && ACTIVE_STATUSES.includes(savedJob.status)) {
        setResumeBanner(savedJob);
        return;
      }
      // 2) altrimenti se c'è un qualsiasi job attivo per il tenant, mostralo
      const activeAny = list.find((j) => ACTIVE_STATUSES.includes(j.status));
      if (activeAny) {
        setResumeBanner(activeAny);
        return;
      }
      // 3) mostra automaticamente l'ultimo job completato se c'è
      const lastDone = list.find((j) => j.status === "done");
      if (lastDone) {
        setLastJobId(lastDone.id);
      }
    })();
  }, [loadJobs]);

  const resumeJob = (job) => {
    setResumeBanner(null);
    setPdfUrl(job.pdf_url || pdfUrl);
    pollJob(job.id);
  };

  const dismissResume = () => setResumeBanner(null);

  const parseNow = async () => {
    setError(null);
    setParsing(true);
    setProgress("Invio richiesta…");
    try {
      const r = await api.post("/ai/parse-catalog-pdf", { pdf_url: pdfUrl });
      const jobId = r.data.job_id;
      toast.success("Job avviato in background — puoi chiudere questa finestra, il lavoro prosegue.");
      // small delay per lasciare al DB il tempo di scrivere lo stato iniziale
      setTimeout(() => pollJob(jobId), 300);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Errore: " + msg);
      setParsing(false);
    }
  };

  const reclassifyAltro = async () => {
    if (!lastJobId) return;
    setReclassifying(true);
    try {
      const r = await api.post(`/ai/parse-job/${lastJobId}/reclassify`, { only_altro: true });
      if (r.data.updated > 0) {
        toast.success(`AI ha riclassificato ${r.data.updated} prodotti`);
        const full = await api.get(`/ai/parse-job/${lastJobId}?include_data=true`);
        setParsed(full.data);
      } else {
        toast.info(r.data.message || "Nessun prodotto da riclassificare");
      }
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setReclassifying(false);
    }
  };

  const applyNow = async () => {
    if (!parsed?.data) return;
    if (!window.confirm("Applicare TUTTE le modifiche senza anteprima? Consigliato: usa 'Rivedi modifiche' invece.")) return;
    setApplying(true);
    try {
      const r = await api.post("/ai/apply-parsed", { data: parsed.data });
      toast.success(`Applicato: ${r.data.applied.tessuti} tessuti · ${r.data.applied.gambe} gambe · ${r.data.applied.metalli} metalli · ${r.data.applied.products_created} prodotti nuovi · ${r.data.applied.products_updated} aggiornati`);
    } catch (e) {
      toast.error("Errore apply: " + (e.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  const openJob = async (job) => {
    if (ACTIVE_STATUSES.includes(job.status)) {
      pollJob(job.id);
      return;
    }
    if (job.status === "done") {
      setLastJobId(job.id);
      const full = await api.get(`/ai/parse-job/${job.id}?include_data=true`);
      setParsed(full.data);
      toast.success("Anteprima job caricata");
    } else {
      setError(job.error || "Job in errore");
      setLastJobId(job.id);
      setParsed(null);
    }
  };

  const deleteJob = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo job dallo storico?")) return;
    try {
      await api.delete(`/ai/parse-job/${jobId}`);
      toast.success("Job eliminato");
      loadJobs();
      if (jobId === lastJobId) {
        setLastJobId(null);
        setParsed(null);
      }
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const s = parsed?.stats || {};
  const d = parsed?.data || {};

  return (
    <AdminLayout title="Importa catalogo da PDF (AI)">
      <div className="max-w-5xl mx-auto p-6 lg:p-10">
        <div className="mb-8">
          <div className="overline text-brand mb-2 inline-flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" /> AI catalog parser · Gemini 2.5 Flash</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight">Importa catalogo da PDF</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            Incolla l&apos;URL del PDF del catalogo. L&apos;AI analizza tutte le pagine, estrae tessuti con TUTTE le varianti colore, gambe, finiture metallo e prodotti. <strong>Il parsing gira sul server:</strong> puoi chiudere la finestra, il lavoro continua e alla riapertura riprende automaticamente da dove era.
          </p>
        </div>

        {/* Banner Resume Job */}
        {resumeBanner && (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="resume-banner">
            <Clock className="w-5 h-5 text-amber-700 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-900">Job in corso rilevato</div>
              <div className="text-amber-800 text-xs mt-0.5">
                Stato: <strong>{resumeBanner.status}</strong>
                {resumeBanner.progress?.step && ` · pass ${resumeBanner.progress.idx}/${resumeBanner.progress.total} (${resumeBanner.progress.step})`}
                {" · avviato "}{formatWhen(resumeBanner.started_at)}
              </div>
            </div>
            <button onClick={() => resumeJob(resumeBanner)} data-testid="resume-btn"
              className="h-9 px-4 rounded-sm bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 inline-flex items-center gap-1.5">
              <Play className="w-3.5 h-3.5" /> Riprendi
            </button>
            <button onClick={dismissResume} className="h-9 w-9 rounded-sm hover:bg-amber-100 inline-flex items-center justify-center" title="Ignora">
              <X className="w-4 h-4 text-amber-700" />
            </button>
          </div>
        )}

        <div className="bg-white border border-border rounded-sm p-5 mb-6">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">URL del PDF</label>
          <Input value={pdfUrl} onChange={(e) => setPdfUrl(e.target.value)} data-testid="pdf-url" className="mt-1 h-10 rounded-sm font-mono text-xs" />
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button onClick={parseNow} disabled={parsing || !pdfUrl} data-testid="parse-btn"
              className="h-11 px-5 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand disabled:opacity-50 inline-flex items-center gap-2">
              {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {parsing ? (progress || "Analisi PDF…") : "Avvia parsing AI"}
            </button>
            <button onClick={reclassifyAltro} disabled={!parsed || reclassifying || !lastJobId} data-testid="reclassify-btn"
              className="h-11 px-5 rounded-sm bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2">
              {reclassifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {reclassifying ? "Riclassifico…" : 'Riclassifica "altro"'}
            </button>
            <button onClick={() => setDiffOpen(true)} disabled={!parsed || !lastJobId} data-testid="review-btn"
              className="h-11 px-5 rounded-sm bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Rivedi modifiche
            </button>
            <button onClick={applyNow} disabled={!parsed || applying} data-testid="apply-btn"
              title="Applica TUTTO senza anteprima (sconsigliato)"
              className="h-11 px-3 rounded-sm border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5">
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
              {applying ? "Applico…" : "Applica tutto (skip preview)"}
            </button>
          </div>
          {parsing && (
            <div className="mt-3 text-xs text-muted-foreground italic">
              💡 Puoi chiudere questa pagina o il browser: il parsing continua sul server. Al ritorno lo riprenderai qui.
            </div>
          )}
        </div>

        {/* Storico job */}
        {jobs.length > 0 && (
          <div className="bg-white border border-border rounded-sm p-4 mb-6" data-testid="jobs-history">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Storico job ({jobs.length})</h3>
            </div>
            <div className="space-y-1">
              {jobs.map((j) => (
                <button
                  key={j.id}
                  onClick={() => openJob(j)}
                  data-testid={`job-row-${j.id}`}
                  className={`w-full flex items-center gap-3 p-2 rounded-sm text-left hover:bg-slate-50 border ${j.id === lastJobId ? "border-brand bg-brand/5" : "border-transparent"}`}
                >
                  <JobStatusPill status={j.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono truncate">{shortUrl(j.pdf_url)}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {formatWhen(j.started_at)}
                      {j.progress?.step && ` · ${j.progress.step} ${j.progress.idx}/${j.progress.total}`}
                      {j.stats?.products && ` · ${j.stats.products} prodotti`}
                    </div>
                  </div>
                  {(j.status === "done" || j.status === "error") && (
                    <button onClick={(e) => deleteJob(j.id, e)} data-testid={`job-del-${j.id}`}
                      className="h-7 w-7 rounded-sm hover:bg-red-100 inline-flex items-center justify-center" title="Elimina">
                      <X className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-start gap-2 text-sm text-red-900">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {parsed && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="parse-stats">
              <Stat label="Tessuti totali" value={s.tessuti_totali} sub={`${s.tessuti_codici_unici} tipi unici`} />
              <Stat label="Gambe" value={s.gambe} />
              <Stat label="Metalli" value={s.metalli} />
              <Stat label="Prodotti" value={s.products} />
              <Stat label="Status" value={<CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto" />} sub="Pronto applica" />
            </div>

            {s.products_per_category && (
              <div className="bg-white border border-border rounded-sm p-4 mb-4">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Distribuzione prodotti per categoria</div>
                <div className="flex flex-wrap gap-1.5" data-testid="parse-categories">
                  {Object.entries(s.products_per_category).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                    <span key={cat} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium ${cat === 'altro' ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-800'}`}>
                      <span className="capitalize">{cat}</span>
                      <span className="font-mono font-bold">{n}</span>
                    </span>
                  ))}
                </div>
                {s.products_per_category.altro > 0 && (
                  <div className="mt-2 text-xs text-amber-800">
                    💡 Hai {s.products_per_category.altro} prodotti in &quot;altro&quot; — usa <strong>Riclassifica</strong> per chiedere all&apos;AI di assegnarli alla categoria corretta.
                  </div>
                )}
              </div>
            )}

            {d.libraries?.tessuti?.length > 0 && (
              <div className="bg-white border border-border rounded-sm p-5 mb-4">
                <h3 className="font-heading font-bold text-lg mb-3">Tessuti estratti (raggruppati)</h3>
                {Object.entries((d.libraries.tessuti || []).reduce((acc, t) => {
                  const k = t.codice || "?";
                  (acc[k] = acc[k] || []).push(t);
                  return acc;
                }, {})).slice(0, 12).map(([tipo, colori]) => (
                  <div key={tipo} className="border-b border-border py-2 last:border-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono font-bold text-sm">{tipo}</span>
                      <span className="text-xs text-muted-foreground">{colori[0]?.livello}</span>
                      <span className="text-xs text-muted-foreground">+ € {colori[0]?.sovrapprezzo?.toFixed(2)}</span>
                      <span className="ml-auto text-xs font-medium">{colori.length} colori</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {colori.slice(0, 20).map((c) => (
                        <span key={c.codice_colore} className="px-1.5 py-0.5 bg-accent/40 rounded text-[10px]">{c.codice_colore} {c.nome_colore}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {d.libraries?.gambe && <PreviewBlock title="Gambe" items={d.libraries.gambe} renderItem={(g) => `${g.codice} — ${g.nome} (+€${(g.sovrapprezzo || 0).toFixed(2)})`} />}
              {d.libraries?.metalli && <PreviewBlock title="Metalli" items={d.libraries.metalli} renderItem={(m) => `${m.codice} — ${m.nome} ${m.hex_color || ""} (+€${(m.sovrapprezzo || 0).toFixed(2)})`} />}
              {d.products && <PreviewBlock title="Prodotti" items={d.products} renderItem={(p) => `${p.sku} — ${p.nome} (€${(p.prezzo_base || 0).toFixed(2)})`} />}
            </div>
          </>
        )}
      </div>

      <DiffPreviewModal
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        parsedData={parsed?.data}
        jobId={lastJobId}
        onApplied={() => loadJobs()}
      />
    </AdminLayout>
  );
}

const STATUS_COLORS = {
  queued: "bg-slate-100 text-slate-700",
  downloading: "bg-blue-100 text-blue-800",
  ai_processing: "bg-amber-100 text-amber-900",
  done: "bg-emerald-100 text-emerald-800",
  error: "bg-red-100 text-red-800",
};

const JobStatusPill = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider ${STATUS_COLORS[status] || "bg-slate-100"}`}>
    {["queued", "downloading", "ai_processing"].includes(status) && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
    {status}
  </span>
);

const shortUrl = (u) => {
  if (!u) return "";
  try {
    const last = u.split("/").pop() || u;
    return decodeURIComponent(last);
  } catch {
    return u.slice(-60);
  }
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "adesso";
    if (diffMin < 60) return `${diffMin} min fa`;
    if (diffMin < 1440) return `${Math.round(diffMin / 60)} h fa`;
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Stat = ({ label, value, sub }) => (
  <div className="bg-white border border-border rounded-sm p-3 text-center">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-heading font-bold text-3xl my-1">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
  </div>
);

const PreviewBlock = ({ title, items, renderItem }) => (
  <div className="bg-white border border-border rounded-sm p-4">
    <h3 className="font-semibold text-sm mb-2 flex items-center justify-between">{title}<span className="text-xs text-muted-foreground">{items.length}</span></h3>
    <div className="space-y-1 text-xs max-h-72 overflow-y-auto">
      {items.slice(0, 50).map((it, i) => (
        <div key={i} className="text-muted-foreground truncate">{renderItem(it)}</div>
      ))}
      {items.length > 50 && <div className="text-[10px] italic text-muted-foreground">…e altri {items.length - 50}</div>}
    </div>
  </div>
);
